"use client";

import { useState } from "react";
import Link from "next/link";
import { useAuth } from "@/lib/auth/AuthContext";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { getAuthErrorMessage } from "@/lib/auth/authErrorMessage";
import { useTenantSettings, useFeatureFlag } from "@/lib/tenant/TenantSettingsProvider";

interface ForgotPasswordPageProps {
    locale: string;
}

/**
 * Forgot password page component.
 */
export function ForgotPasswordPage({ locale }: ForgotPasswordPageProps) {
    const labels = useLabels();
    const { isMultiLang } = useTenantSettings();
    const isAuthFeatureEnabled = useFeatureFlag("authentication");
    const { resetPassword, isAuthEnabled } = useAuth();

    const [email, setEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const t = labels.auth?.forgotPassword || {};
    // Only prefix URLs with locale if tenant has multiple languages
    const basePath = isMultiLang && locale ? `/${locale}` : "";

    if (!isAuthFeatureEnabled || !isAuthEnabled) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4">
                <div className="text-center text-muted-foreground">Authentication is not configured for this site.</div>
            </div>
        );
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError("");
        setSuccess(false);

        try {
            await resetPassword(email);
            setSuccess(true);
        } catch (err: unknown) {
            setError(getAuthErrorMessage(err, labels) ?? "");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 bg-background">
                <div className="max-w-md w-full space-y-8 text-center">
                    <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-foreground">{t.emailSent || "Check your email"}</h1>
                    <p className="text-muted-foreground">
                        {t.emailSentDescription || "We've sent password reset instructions to your email address."}
                    </p>
                    <Link
                        prefetch={false}
                        href={`${basePath}/sign-in`}
                        className="inline-block py-3 px-6 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition cursor-pointer"
                    >
                        {t.backToSignIn || "Back to Sign In"}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 bg-background">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-foreground">{t.title || "Reset Password"}</h1>
                    <p className="mt-2 text-muted-foreground">
                        {t.subtitle || "Enter your email to receive reset instructions"}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
                            {t.email || "Email Address"}
                        </label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full px-3 py-2 input"
                            placeholder="you@example.com"
                        />
                    </div>

                    {error && (
                        <div className="text-red-500 text-sm text-center bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-3 px-4 font-medium btn-primary disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
                    >
                        {isSubmitting && (
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                            </svg>
                        )}
                        {isSubmitting ? t.sending || "Sending..." : t.sendButton || "Send Reset Instructions"}
                    </button>
                </form>

                <div className="text-center">
                    <Link
                        prefetch={false}
                        href={`${basePath}/sign-in`}
                        className="text-sm text-primary hover:underline cursor-pointer"
                    >
                        {t.backToSignIn || "Back to Sign In"}
                    </Link>
                </div>
            </div>
        </div>
    );
}
